/**
 * Visibility Resolver
 * Evaluates visibility conditions for form controls based on different criteria:
 * - Mode-based visibility (edit, view, design, admin, preview)
 * - Expression-based visibility (JavaScript conditions)
 * - Dependency-based visibility (field dependencies)
 * - Always/never visibility
 */

import type { FormControl, OperatingMode } from '@atlas-forms/types-js';

/**
 * Visibility rule definition for controls
 */
export interface VisibilityRule {
  /** Type of visibility rule */
  type: 'always' | 'never' | 'expression' | 'mode-based' | 'dependency';

  /** JavaScript expression condition (e.g., "{{country === 'US'}}") */
  condition?: string;

  /** Operating modes this rule applies to */
  modes?: OperatingMode[];

  /** Field IDs this control depends on */
  dependsOn?: string[];
}

/**
 * Expression evaluator for parsing and evaluating JavaScript conditions
 */
export interface ExpressionEvaluator {
  /**
   * Evaluate a condition expression against values
   * @param condition JavaScript expression (e.g., "{{country === 'US'}}")
   * @param context Evaluation context with values and other data
   * @returns True if condition evaluates to truthy, false otherwise
   */
  evaluateCondition(condition: string, context: Record<string, any>): boolean;

  /**
   * Check if expression is valid JavaScript syntax
   * @param condition JavaScript expression
   * @returns True if valid, false otherwise
   */
  isValid(condition: string): boolean;
}

/**
 * Cache statistics for monitoring memory usage
 */
interface CacheStats {
  hits: number;
  misses: number;
  evictions: number;
  currentSize: number;
}

/**
 * Safe expression evaluator using strict whitelist approach
 * Evaluates only comparison and logical operators without Function() constructor
 * Prevents code injection attacks
 */
export class SafeExpressionEvaluator implements ExpressionEvaluator {
  private cache: Map<string, (context: Record<string, any>) => boolean> = new Map();
  private cacheOrder: string[] = []; // Track insertion order for LRU eviction
  private maxCacheSize = 50; // Conservative limit to prevent memory bloat
  private cacheStats: CacheStats = { hits: 0, misses: 0, evictions: 0, currentSize: 0 };

  /**
   * Disallowed patterns that indicate code injection attempts
   */
  private readonly INJECTION_PATTERNS = [
    /\beval\b/i,
    /\bFunction\b/i,
    /\brequire\b/i,
    /\bimport\b/i,
    /\bexport\b/i,
    /\bthis\b/i,
    /\bwindow\b/i,
    /\bdocument\b/i,
    /\bconstructor\b/i,
    /\bprototype\b/i,
    /\b__proto__\b/i,
  ];

  evaluateCondition(condition: string, context: Record<string, any>): boolean {
    try {
      // Extract JavaScript from {{ }} template syntax
      const cleanCondition = this.extractExpression(condition);

      // Validate condition for injection attempts
      if (!this.isValid(cleanCondition)) {
        console.warn(`Invalid visibility condition: ${cleanCondition}`);
        return true; // Default to visible on invalid condition
      }

      // Check cache
      let evaluator = this.cache.get(cleanCondition);

      if (evaluator) {
        this.cacheStats.hits++;
      } else {
        this.cacheStats.misses++;

        // Build evaluator function safely
        evaluator = this.buildEvaluator(cleanCondition);

        // Cache with LRU eviction strategy
        this.cache.set(cleanCondition, evaluator);
        this.cacheOrder.push(cleanCondition);

        // Check if we need to evict (LRU strategy)
        if (this.cache.size >= this.maxCacheSize) {
          // Remove oldest entry (first in cacheOrder array)
          const oldestKey = this.cacheOrder.shift();
          if (oldestKey) {
            this.cache.delete(oldestKey);
            this.cacheStats.evictions++;
          }
        }

        this.cacheStats.currentSize = this.cache.size;
      }

      // Evaluate with provided values
      const values = context.values || context;
      const result = evaluator(values);
      return Boolean(result);
    } catch (error) {
      console.error(`Error evaluating visibility condition: ${condition}`, error);
      return true; // Default to visible on error
    }
  }

  isValid(condition: string): boolean {
    try {
      // Extract expression
      const cleanCondition = this.extractExpression(condition);

      // Check for injection patterns
      for (const pattern of this.INJECTION_PATTERNS) {
        if (pattern.test(cleanCondition)) {
          console.warn(`Injection pattern detected in condition: ${pattern.source}`);
          return false;
        }
      }

      // Validate tokens contain only safe operators and identifiers
      return this.validateTokens(cleanCondition);
    } catch {
      return false;
    }
  }

  /**
   * Extract JavaScript from template syntax {{ }}
   */
  private extractExpression(condition: string): string {
    const match = condition.match(/^{{\s*(.*?)\s*}}$/);
    return match ? match[1] : condition;
  }

  /**
   * Validate that tokens only contain allowed operators and identifiers
   * @param condition Condition to validate
   * @returns True if valid, false otherwise
   */
  private validateTokens(condition: string): boolean {
    // Remove string literals (single and double quoted)
    const withoutStrings = condition.replace(/'([^'\\]|\\.)*'/g, '"str"')
                                    .replace(/"([^"\\]|\\.)*"/g, '"str"');

    // Check for allowed operators and valid identifiers
    const tokenRegex = /[\w$]+|===|!==|==|!=|<=|>=|<|>|&&|\|\||!|\?|:|[()]/g;
    const tokens = withoutStrings.match(tokenRegex) || [];
    const reconstructed = tokens.join('');

    // If reconstructed doesn't match input (without whitespace), there are unknown tokens
    const conditionNoSpace = withoutStrings.replace(/\s+/g, '');
    return reconstructed === conditionNoSpace;
  }

  /**
   * Build a safe evaluator function for the condition
   * Uses safe tokenization and parsing without Function() constructor
   * @param condition JavaScript condition (already validated)
   * @returns Evaluator function
   */
  private buildEvaluator(
    condition: string
  ): (context: Record<string, any>) => boolean {
    return (context: Record<string, any>): boolean => {
      try {
        // Parse and evaluate condition safely without Function()
        return this.safeEvaluate(condition, context);
      } catch (error) {
        console.error('Error in expression evaluation:', error);
        return true;
      }
    };
  }

  /**
   * Safely evaluate an expression using a custom parser
   * Does not use eval() or Function() constructor
   * @param condition Expression to evaluate
   * @param context Variables available in scope
   * @returns Boolean result of evaluation
   */
  private safeEvaluate(condition: string, context: Record<string, any>): boolean {
    // Tokenize the condition
    const tokens = this.tokenize(condition);

    // Parse and evaluate the tokens
    const result = this.parseExpression(tokens, context);

    return Boolean(result);
  }

  /**
   * Tokenize a condition string into safe tokens
   * @param condition Expression to tokenize
   * @returns Array of tokens
   */
  private tokenize(condition: string): string[] {
    // Token patterns for operators and literals
    const tokenPatterns = [
      { type: 'number', pattern: /-?\d+\.?\d*/ },
      { type: 'string', pattern: /"([^"\\]|\\.)*"|'([^'\\]|\\.)*'/ },
      { type: 'identifier', pattern: /[a-zA-Z_$][a-zA-Z0-9_$]*/ },
      { type: 'operator', pattern: /===|!==|==|!=|<=|>=|&&|\|\||<|>|!|\?|:/ },
      { type: 'paren', pattern: /[(){}[\]]/ },
      { type: 'whitespace', pattern: /\s+/ },
    ];

    const tokens: string[] = [];
    let position = 0;

    while (position < condition.length) {
      let matched = false;

      for (const { pattern } of tokenPatterns) {
        const regex = new RegExp(`^${pattern.source}`);
        const match = condition.substring(position).match(regex);

        if (match) {
          const token = match[0];
          if (!/^\s+$/.test(token)) { // Skip whitespace tokens
            tokens.push(token);
          }
          position += token.length;
          matched = true;
          break;
        }
      }

      if (!matched) {
        throw new Error(`Invalid token at position ${position}: ${condition[position]}`);
      }
    }

    return tokens;
  }

  /**
   * Parse and evaluate tokens into a boolean result
   * Uses recursive descent parser for safe evaluation
   * @param tokens Array of tokens from tokenize()
   * @param context Variables available in scope
   * @returns Evaluated result
   */
  private parseExpression(
    tokens: string[],
    context: Record<string, any>
  ): boolean {
    const parser = new ExpressionParser(tokens, context);
    return parser.parseLogicalOr();
  }
}

/**
 * Safe expression parser using recursive descent parsing
 * Prevents code injection by only parsing allowed operators
 */
class ExpressionParser {
  private tokens: string[];
  private position: number;
  private context: Record<string, any>;

  constructor(tokens: string[], context: Record<string, any>) {
    this.tokens = tokens;
    this.position = 0;
    this.context = context;
  }

  parseLogicalOr(): boolean {
    let left = this.parseLogicalAnd();

    while (this.peek() === '||') {
      this.consume();
      const right = this.parseLogicalAnd();
      left = left || right;
    }

    return left;
  }

  parseLogicalAnd(): boolean {
    let left = this.parseTernary();

    while (this.peek() === '&&') {
      this.consume();
      const right = this.parseTernary();
      left = left && right;
    }

    return left;
  }

  parseTernary(): boolean {
    let condition = this.parseComparison();

    if (this.peek() === '?') {
      this.consume(); // consume '?'
      const trueValue = this.parseComparison();
      this.expectAndConsume(':');
      const falseValue = this.parseComparison();
      return condition ? trueValue : falseValue;
    }

    return condition;
  }

  parseComparison(): boolean {
    let left = this.parseUnary();

    const operator = this.peek();
    if (operator && ['===', '!==', '==', '!=', '<', '>', '<=', '>='].includes(operator)) {
      this.consume();
      const right = this.parseUnary();
      return this.compare(left, operator, right);
    }

    return Boolean(left);
  }

  parseUnary(): any {
    if (this.peek() === '!') {
      this.consume();
      return !this.parseUnary();
    }

    return this.parsePrimary();
  }

  parsePrimary(): any {
    const token = this.peek();

    if (!token) {
      throw new Error('Unexpected end of expression');
    }

    // Parentheses
    if (token === '(') {
      this.consume();
      const result = this.parseLogicalOr();
      this.expectAndConsume(')');
      return result;
    }

    // String literal
    if (token.startsWith('"') || token.startsWith("'")) {
      this.consume();
      return this.parseStringLiteral(token);
    }

    // Number literal
    if (/^-?\d+\.?\d*$/.test(token)) {
      this.consume();
      return Number(token);
    }

    // Identifier (variable from context)
    if (/^[a-zA-Z_$][a-zA-Z0-9_$]*$/.test(token)) {
      this.consume();
      return this.context[token];
    }

    throw new Error(`Unexpected token: ${token}`);
  }

  private parseStringLiteral(token: string): string {
    // Remove quotes and handle escape sequences
    const content = token.slice(1, -1);
    return content
      .replace(/\\"/g, '"')
      .replace(/\\'/g, "'")
      .replace(/\\\\/g, '\\')
      .replace(/\\n/g, '\n')
      .replace(/\\r/g, '\r')
      .replace(/\\t/g, '\t');
  }

  private compare(left: any, operator: string, right: any): boolean {
    switch (operator) {
      case '===':
        return left === right;
      case '!==':
        return left !== right;
      case '==':
        return left == right;
      case '!=':
        return left != right;
      case '<':
        return left < right;
      case '>':
        return left > right;
      case '<=':
        return left <= right;
      case '>=':
        return left >= right;
      default:
        throw new Error(`Unknown operator: ${operator}`);
    }
  }

  private peek(): string | undefined {
    return this.tokens[this.position];
  }

  private consume(): string {
    return this.tokens[this.position++];
  }

  private expectAndConsume(expected: string): void {
    if (this.peek() !== expected) {
      throw new Error(`Expected '${expected}', got '${this.peek()}'`);
    }
    this.consume();
  }
}

/**
 * Check if a control should be visible based on all criteria
 * @param control Control to check
 * @param values Current form values
 * @param mode Operating mode
 * @param evaluator Expression evaluator
 * @returns True if control should be visible, false otherwise
 */
export function isControlVisible(
  control: FormControl,
  values: Record<string, any>,
  mode: OperatingMode,
  evaluator?: ExpressionEvaluator
): boolean {
  // Check basic hidden property first
  if (control.hidden) {
    return false;
  }

  // Check visibility rule
  if (control.visibilityRule) {
    const rule = control.visibilityRule;

    // 'never' type is always hidden
    if (rule.type === 'never') {
      return false;
    }

    // 'always' type is always visible
    if (rule.type === 'always') {
      return true;
    }

    // 'mode-based' visibility
    if (rule.type === 'mode-based' && rule.modes) {
      const modeAllowed = rule.modes.includes(mode);
      if (!modeAllowed) {
        return false;
      }
    }

    // 'expression' based visibility
    if (rule.type === 'expression' && rule.condition && evaluator) {
      const isVisible = evaluator.evaluateCondition(rule.condition, { values });
      if (!isVisible) {
        return false;
      }
    }

    // 'dependency' based visibility
    if (rule.type === 'dependency' && rule.dependsOn) {
      // All dependencies must have truthy values to show this control
      const allDepsPresent = rule.dependsOn.every((depId) => {
        const depValue = values[depId];
        return depValue !== undefined && depValue !== null && depValue !== '';
      });

      if (!allDepsPresent) {
        return false;
      }
    }
  }

  // Check mode-based visibility settings
  if (control.modeVisibilitySettings) {
    const modeKey = mode as keyof typeof control.modeVisibilitySettings;
    const modeVisibility = control.modeVisibilitySettings[modeKey];

    // false means hidden in this mode
    if (modeVisibility === false) {
      return false;
    }
  }

  // Check base visibility property
  if (control.visibility === 'edit-only' && (mode === 'view' || mode === 'preview')) {
    return false;
  }

  if (control.visibility === 'view-only' && mode === 'edit') {
    return false;
  }

  // Default: visible
  return true;
}

/**
 * Get visibility state for a control (not just boolean, but the specific state)
 * @param control Control to check
 * @param values Current form values
 * @param mode Operating mode
 * @param evaluator Expression evaluator
 * @returns 'visible', 'hidden', or 'disabled' (disabled is visible but not editable)
 */
export function getControlVisibilityState(
  control: FormControl,
  values: Record<string, any>,
  mode: OperatingMode,
  evaluator?: ExpressionEvaluator
): 'visible' | 'hidden' | 'readonly' {
  // First check if control is visible
  if (!isControlVisible(control, values, mode, evaluator)) {
    return 'hidden';
  }

  // Check if control should be readonly based on mode
  if ((mode === 'view' || mode === 'preview') && control.visibility !== 'edit-only') {
    return 'readonly';
  }

  return 'visible';
}

/**
 * Resolve all visible controls from a schema with their filtered sections
 * @param controls All controls in schema
 * @param values Current form values
 * @param mode Operating mode
 * @param evaluator Expression evaluator
 * @returns Filtered list of visible controls
 */
export function resolveVisibleControls(
  controls: FormControl[],
  values: Record<string, any>,
  mode: OperatingMode,
  evaluator?: ExpressionEvaluator
): FormControl[] {
  return controls.filter((control) => isControlVisible(control, values, mode, evaluator));
}

/**
 * Build a dependency graph of all field dependencies
 * Useful for understanding relationships between fields
 * @param controls All controls in schema
 * @returns Map of control ID to its dependent control IDs
 */
export function buildDependencyGraph(controls: FormControl[]): Map<string, Set<string>> {
  const dependencyGraph = new Map<string, Set<string>>();

  controls.forEach((control) => {
    if (control.visibilityRule?.type === 'dependency' && control.visibilityRule.dependsOn) {
      const dependsOn = new Set(control.visibilityRule.dependsOn);
      dependencyGraph.set(control.id, dependsOn);
    }
  });

  return dependencyGraph;
}

/**
 * Get all controls affected by a specific field change
 * @param fieldId Field that changed
 * @param controls All controls in schema
 * @returns Array of controls that depend on this field
 */
export function getControlsAffectedByField(fieldId: string, controls: FormControl[]): FormControl[] {
  return controls.filter((control) => {
    if (control.visibilityRule?.type === 'dependency' && control.visibilityRule.dependsOn) {
      return control.visibilityRule.dependsOn.includes(fieldId);
    }
    return false;
  });
}

/**
 * Validate visibility rules for logical consistency
 * @param controls All controls in schema
 * @param evaluator Expression evaluator
 * @returns Array of validation errors (empty if valid)
 */
export function validateVisibilityRules(
  controls: FormControl[],
  evaluator?: ExpressionEvaluator
): string[] {
  const errors: string[] = [];
  const controlIds = new Set(controls.map((c) => c.id));

  controls.forEach((control) => {
    if (!control.visibilityRule) return;

    const rule = control.visibilityRule;

    // Check expression syntax
    if (rule.type === 'expression' && rule.condition && evaluator) {
      if (!evaluator.isValid(rule.condition)) {
        errors.push(`Control '${control.id}': Invalid expression condition: ${rule.condition}`);
      }
    }

    // Check dependencies exist
    if (rule.type === 'dependency' && rule.dependsOn) {
      rule.dependsOn.forEach((depId) => {
        if (!controlIds.has(depId)) {
          errors.push(
            `Control '${control.id}': Dependency on non-existent field '${depId}'`
          );
        }
      });
    }

    // Check for circular dependencies
    if (rule.type === 'dependency' && rule.dependsOn) {
      const visited = new Set<string>();
      const visiting = new Set<string>();

      const hasCycle = (id: string): boolean => {
        if (visiting.has(id)) return true;
        if (visited.has(id)) return false;

        visiting.add(id);

        const ctrl = controls.find((c) => c.id === id);
        if (ctrl?.visibilityRule?.type === 'dependency' && ctrl.visibilityRule.dependsOn) {
          for (const depId of ctrl.visibilityRule.dependsOn) {
            if (hasCycle(depId)) return true;
          }
        }

        visiting.delete(id);
        visited.add(id);
        return false;
      };

      if (hasCycle(control.id)) {
        errors.push(`Control '${control.id}': Circular dependency detected`);
      }
    }
  });

  return errors;
}
